/**
 * The stop banner under the AI Service page header (task 1164), on every tab.
 *
 * Amber for a stop the owner chose: who, when and why. Red for the circuit
 * breaker: the failure count, the last error, a link to each failed run in the
 * Run Explorer, and how many calls have been refused since. Start (green) in
 * both; it opens the Start confirmation the page owns (task 1191 renamed the
 * label; the prop, test id and route keep `resume`).
 */
import { Link } from 'react-router-dom';
import { ExclamationTriangleIcon, PauseCircleIcon, PlayIcon } from '@heroicons/react/24/outline';
import { serviceRunPath } from '@/components/runExplorer/runExplorerLinks';
import { UserName } from '@/components/ui/UserName';
import { useRefusedSinceStop } from '@/hooks/useEndpoints';
import { useFlag } from '@/hooks/useFeatureFlags';
import type { Endpoint } from '@/types';
import { formatStopInstant, NO_STOP_REASON } from './serviceStop';

interface ServiceStopBannerProps {
  endpoint: Endpoint;
  onResume: () => void;
}

export function ServiceStopBanner(props: ServiceStopBannerProps) {
  // Stop and Start's lever (task 1216) hides the banner with the actions.
  const stopEnabled = useFlag('innovate.fe.services.stop');
  return stopEnabled ? <ServiceStopBannerContent {...props} /> : null;
}

function ServiceStopBannerContent({ endpoint, onResume }: ServiceStopBannerProps) {
  const stop = endpoint.stop ?? null;
  const refused = useRefusedSinceStop(stop ? endpoint.id : undefined, stop?.stoppedAt);
  if (!stop) return null;

  const automatic = stop.kind === 'auto';
  const reason = stop.reason?.trim();
  const runIds = stop.collaborationIds ?? [];

  const resumeButton = (
    <button
      type="button"
      data-testid="service-stop-banner-resume"
      onClick={onResume}
      className="inline-flex shrink-0 items-center rounded-md bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700"
    >
      <PlayIcon className="-ml-0.5 mr-1.5 h-4 w-4" aria-hidden="true" />
      Start
    </button>
  );

  return (
    <div
      role="status"
      data-testid="service-stop-banner"
      data-kind={stop.kind}
      className={`mt-4 rounded-lg border p-4 ${
        automatic
          ? 'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-900/20'
          : 'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20'
      }`}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 gap-3">
          {automatic ? (
            <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-red-600 dark:text-red-400" aria-hidden="true" />
          ) : (
            <PauseCircleIcon className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden="true" />
          )}
          <div className="min-w-0 space-y-1 text-sm">
            {automatic ? (
              <>
                <p data-testid="service-stop-banner-summary" className="font-medium text-red-900 dark:text-red-100">
                  Stopped automatically on {formatStopInstant(stop.stoppedAt)}:{' '}
                  {stop.failureCount ?? runIds.length} consecutive failures — last error:{' '}
                  <span className="font-mono text-xs">{stop.lastError ?? 'not recorded'}</span>
                </p>
                {runIds.length > 0 && (
                  <p data-testid="service-stop-banner-runs" className="text-red-800 dark:text-red-200">
                    Failed runs:{' '}
                    {runIds.map((id, index) => (
                      <span key={id}>
                        {index > 0 && ' · '}
                        <Link
                          to={serviceRunPath(endpoint.id, id)}
                          className="font-mono text-xs font-medium underline hover:no-underline"
                        >
                          {id.slice(0, 8)}
                        </Link>
                      </span>
                    ))}
                  </p>
                )}
                {refused.data && (
                  <p data-testid="service-stop-banner-refused" className="text-red-800 dark:text-red-200">
                    Since then {refused.data.count}
                    {refused.data.capped ? '+' : ''} call{refused.data.count === 1 ? '' : 's'}{' '}
                    refused with 409.
                  </p>
                )}
                <p className="text-red-800 dark:text-red-200">
                  Fix the cause, verify with a test on the Testing tab, then start it again. Start
                  resets the failure count to zero.
                </p>
              </>
            ) : (
              <>
                <p data-testid="service-stop-banner-summary" className="font-medium text-amber-900 dark:text-amber-100">
                  Stopped by <UserName userId={stop.stoppedBy} /> on {formatStopInstant(stop.stoppedAt)}:{' '}
                  {reason ? `“${reason}”` : NO_STOP_REASON}
                </p>
                <p className="text-amber-800 dark:text-amber-200">
                  Outside callers receive <code className="font-mono">409 service_stopped</code>;
                  your team can still use it in the app. Its Schedules are paused. Configuration, URL, versions and history are unchanged. Test
                  runs still work.
                </p>
              </>
            )}
          </div>
        </div>
        {resumeButton}
      </div>
    </div>
  );
}

export default ServiceStopBanner;
